import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  matchFileFormatToOptionValue,
  OGC_FEATURE_DATASET_TYPE,
  OGC_FEATURE_FILE_FORMAT,
} from '../../metadata-json/dataset-metadata-template.util';
import type { NgsildMetadataInfo, NgsildYearRange } from '../ngsild.models';
import type { DataDescriptorFormRow, DropdownItem } from '../data-descriptor.models';
import {
  addDataDescriptorRow,
  canSaveDataDescriptors,
  clearDataDescriptorDraft,
  isDataDescriptorRowComplete,
  createEmptyDataDescriptorRow,
  deepCopyRows,
  getSchemaTypeId,
  initDataDescriptors,
  isNumberSchema,
  PARAMETER_TYPE_OPTIONS,
  refreshAttributeNameDuplicateFlags,
  removeDataDescriptorRow,
  saveDataDescriptorDraft,
  SCHEMA_TYPE_OPTIONS,
  selectDropdownValue,
  validateDataDescriptors,
} from '../data-descriptor.logic';
import {
  SAMPLE_JSON_MAX_BYTES,
  formatSampleDataJson,
  parseSampleDataJsonFile,
  utf8ByteLength,
} from '../sample-data.logic';
import { YearRangeFieldComponent } from '../year-range-field/year-range-field.component';
import {
  AdditionalInfoComponent,
  type AdditionalInfoFeedbackItem,
} from '../../../shared/additional-info/additional-info.component';
import type { TemplateAdditionalInfoState } from '../../../shared/additional-info/additional-info.logic';

type DropdownKey = 'license' | 'uploadFrequency' | 'datasetType' | 'fileFormat';

interface SelectOption {
  value: string;
  label: string;
}

const DATASET_TYPE_STRUCTURED = 'Structured';
const DATASET_TYPE_SEMI = 'semi Structured';
const DATASET_TYPE_UNSTRUCTURED = 'unStructured';

function tagsRequired(control: AbstractControl): ValidationErrors | null {
  const tags = control.value as string[] | null;
  if (!Array.isArray(tags) || tags.length === 0) {
    return { tagsRequired: true };
  }
  return null;
}

function yearRangeRequired(control: AbstractControl): ValidationErrors | null {
  const v = control.value as NgsildYearRange | null;
  if (v?.startYear == null || v?.endYear == null) {
    return { yearRangeRequired: true };
  }
  return null;
}

@Component({
  selector: 'lib-ngsild-metadata-info',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, YearRangeFieldComponent, AdditionalInfoComponent],
  templateUrl: './metadata-info.component.html',
  styleUrl: './metadata-info.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'ngsild-widget-host',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetadataInfoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly validChange = output<boolean>();
  readonly valueChange = output<NgsildMetadataInfo>();
  readonly additionalInfoRef = viewChild(AdditionalInfoComponent);

  /**
   * When true, dataset type is locked to "semi Structured" and file format to "gpkg".
   * Set this for OGC Feature dataset onboarding only.
   */
  readonly isOgcFeature = input<boolean>(false);

  readonly openDropdown = signal<DropdownKey | null>(null);
  tagInput = '';
  readonly tagMaxLength = 20;

  // ── Dropdown options (main form) ─────────────────────────────────────────

  readonly licenseOptions: SelectOption[] = [
    { value: 'Open Government License, India', label: 'Open Government License, India' },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'Other', label: 'Other' },
  ];

  readonly uploadFrequencyOptions: SelectOption[] = [
    { value: 'OneTime', label: 'OneTime' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'Annually', label: 'Annually' },
  ];

  readonly datasetTypeOptions: SelectOption[] = [
    { value: DATASET_TYPE_STRUCTURED, label: 'Structured' },
    { value: DATASET_TYPE_SEMI, label: 'semi Structured' },
    { value: DATASET_TYPE_UNSTRUCTURED, label: 'unStructured' },
  ];

  fileFormatOptions: SelectOption[] = [];
  private fileFormatMap: Record<string, SelectOption[]> = {};

  // ── Main form ────────────────────────────────────────────────────────────

  readonly form = this.fb.nonNullable.group({
    tags: this.fb.nonNullable.control<string[]>([], [tagsRequired]),
    yearRange: this.fb.control<NgsildYearRange | null>(null, [yearRangeRequired]),
    geoCoverage: ['', Validators.required],
    uploadFrequency: ['', Validators.required],
    license: ['', Validators.required],
    otherLicense: [''],
    datasetType: ['', Validators.required],
    fileFormat: [{ value: '', disabled: true }, Validators.required],
  });

  // ── Data Descriptor state ────────────────────────────────────────────────

  readonly parameterTypeOptions = PARAMETER_TYPE_OPTIONS;
  readonly schemaTypeOptions = SCHEMA_TYPE_OPTIONS;

  /** Persisted/displayed rows (reflects last Save or localStorage restore). */
  descriptorRows: DataDescriptorFormRow[] = [];

  /** Working copy while the editor is open. */
  editorRows: DataDescriptorFormRow[] = [];

  isDescriptorEditorOpen = false;

  /** Tracks which dropdown inside the editor is open: "rowIndex:field" */
  readonly openEditorDropdown = signal<string | null>(null);

  /** Dropdown panel moved to document.body to escape modal overflow clipping. */
  private editorDropdownPortal: HTMLElement | null = null;

  /** Selected descriptor row in the editor modal (left list → right panel). */
  readonly selectedEditorRowIndex = signal<number | null>(null);

  /** Row index with open Action dropdown in the descriptors table. */
  readonly openTableActionRow = signal<number | null>(null);

  // ── Sample Data state ────────────────────────────────────────────────────

  /** Final payload value sent on create (null = use server stub fallback). */
  dataSample: unknown | null = null;

  sampleUploadError: string | null = null;
  sampleEditorError: string | null = null;

  /** Set when sample data came from Upload JSON. */
  sampleUploadedFileName: string | null = null;

  /** Max Upload JSON / paste size (2 MB). */
  readonly sampleJsonMaxBytes = SAMPLE_JSON_MAX_BYTES;

  isSampleEditorOpen = false;
  isSamplePreviewOpen = false;
  sampleEditorText = '';

  private static readonly BODY_SCROLL_LOCK_CLASS = 'ngsild-modal-scroll-lock';

  ngOnInit(): void {
    this.descriptorRows = initDataDescriptors();

    this.destroyRef.onDestroy(() => {
      this.unlockBodyScroll();
      this.teardownEditorDropdownPortal();
    });

    void this.loadFileFormatMap();

    if (this.isOgcFeature()) {
      this.applyOgcFeatureDefaults();
    }

    this.form
      .get('datasetType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((datasetType) => {
        const fileFormatControl = this.form.get('fileFormat');
        if (datasetType) {
          this.fileFormatOptions = this.fileFormatMap[datasetType] ?? [];
          fileFormatControl?.enable();
          fileFormatControl?.setValue('');
          fileFormatControl?.markAsUntouched();
        } else {
          this.fileFormatOptions = [];
          fileFormatControl?.disable();
          fileFormatControl?.setValue('');
        }
        this.cdr.markForCheck();
      });

    this.form
      .get('license')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((license) => {
        const otherLicenseControl = this.form.get('otherLicense');
        if (license === 'Other') {
          otherLicenseControl?.setValidators([Validators.required]);
        } else {
          otherLicenseControl?.clearValidators();
          otherLicenseControl?.setValue('');
        }
        otherLicenseControl?.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      });

    this.emitState();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitState());
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.validChange.emit(this.form.valid));
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeTableActionDropdown();
    if (this.openEditorDropdown()) {
      this.closeEditorDropdown();
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.editorDropdownPortal?.contains(target)) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(target)) {
      let changed = false;
      if (this.openDropdown()) {
        this.openDropdown.set(null);
        changed = true;
      }
      if (this.openEditorDropdown()) {
        this.closeEditorDropdown();
        changed = true;
      }
      if (this.openTableActionRow() !== null) {
        this.openTableActionRow.set(null);
        changed = true;
      }
      if (changed) {
        this.cdr.markForCheck();
      }
    }
  }

  // ── Main form dropdown helpers ───────────────────────────────────────────

  isDropdownOpen(key: DropdownKey): boolean {
    return this.openDropdown() === key;
  }

  toggleDropdown(key: DropdownKey): void {
    if (key === 'fileFormat' && this.isFileFormatDisabled()) return;
    if (this.isOgcFeature() && (key === 'datasetType' || key === 'fileFormat')) return;
    this.openDropdown.update((current) => (current === key ? null : key));
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    this.openDropdown.set(null);
    this.cdr.markForCheck();
  }

  selectOption(
    key: DropdownKey,
    option: SelectOption,
    controlName: 'license' | 'uploadFrequency' | 'datasetType' | 'fileFormat',
  ): void {
    if (key === 'datasetType') {
      this.form.patchValue({ datasetType: option.value });
    } else {
      this.form.patchValue({ [controlName]: option.value });
    }
    this.form.get(controlName)?.markAsTouched();
    this.closeDropdown();
    this.emitState();
  }

  isSelected(key: DropdownKey, value: string): boolean {
    switch (key) {
      case 'license':
        return this.form.controls.license.value === value;
      case 'uploadFrequency':
        return this.form.controls.uploadFrequency.value === value;
      case 'datasetType':
        return this.form.controls.datasetType.value === value;
      case 'fileFormat': {
        const cur = this.form.controls.fileFormat.value;
        return String(cur).toLowerCase() === String(value).toLowerCase();
      }
      default:
        return false;
    }
  }

  getSelectedLabel(key: DropdownKey): string {
    const find = (options: SelectOption[], value: string) =>
      options.find((o) => o.value === value)?.label ?? '';

    switch (key) {
      case 'license':
        return find(this.licenseOptions, this.form.controls.license.value);
      case 'uploadFrequency':
        return find(this.uploadFrequencyOptions, this.form.controls.uploadFrequency.value);
      case 'datasetType':
        return find(this.datasetTypeOptions, this.form.controls.datasetType.value);
      case 'fileFormat': {
        const raw = this.form.controls.fileFormat.value;
        const option = this.fileFormatOptions.find(
          (o) =>
            o.value === raw ||
            o.value.toLowerCase() === String(raw).toLowerCase(),
        );
        return option?.label ?? '';
      }
      default:
        return '';
    }
  }

  isFileFormatDisabled(): boolean {
    return this.form.controls.fileFormat.disabled;
  }

  fileFormatPlaceholder(): string {
    return this.isFileFormatDisabled() ? 'Select dataset type first' : 'Select file format';
  }

  isOtherLicenseSelected(): boolean {
    return this.form.controls.license.value === 'Other';
  }

  // ── Tag helpers ──────────────────────────────────────────────────────────

  get tags(): string[] {
    return this.form.controls.tags.value;
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput).slice(0, this.tagMaxLength);
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const current = [...this.form.controls.tags.value];
    if (!current.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      current.push(tag);
      this.form.patchValue({ tags: current });
      this.form.controls.tags.markAsTouched();
    }
    this.tagInput = '';
    this.emitState();
    this.cdr.markForCheck();
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  removeTag(tag: string): void {
    const updated = this.form.controls.tags.value.filter((t) => t !== tag);
    this.form.patchValue({ tags: updated });
    this.form.controls.tags.markAsTouched();
    this.emitState();
    this.cdr.markForCheck();
  }

  showError(
    controlName:
      | 'tags'
      | 'yearRange'
      | 'geoCoverage'
      | 'uploadFrequency'
      | 'license'
      | 'otherLicense'
      | 'datasetType'
      | 'fileFormat',
  ): boolean {
    const c = this.form.get(controlName);
    return !!(c && c.invalid && c.touched);
  }

  // ── Data Descriptor editor ───────────────────────────────────────────────

  deleteDescriptorRow(index: number): void {
    this.descriptorRows = this.descriptorRows.filter((_, i) => i !== index);
    if (this.descriptorRows.length === 0) {
      this.descriptorRows = [createEmptyDataDescriptorRow()];
      clearDataDescriptorDraft();
    } else {
      localStorage.setItem('dataDescriptors', JSON.stringify(this.descriptorRows));
    }
    this.emitState();
    this.cdr.markForCheck();
  }

  toggleTableActionDropdown(event: MouseEvent, index: number): void {
    event.stopPropagation();
    const wasOpen = this.openTableActionRow() === index;
    this.openTableActionRow.update((cur) => (cur === index ? null : index));
    this.cdr.detectChanges();

    if (!wasOpen && this.openTableActionRow() === index) {
      queueMicrotask(() => this.positionTableActionDropdown(event, index));
    }
  }

  private positionTableActionDropdown(event: MouseEvent, index: number): void {
    const button = event.currentTarget as HTMLElement;
    const dropdown = this.elementRef.nativeElement.querySelector(
      `[data-ngsild-action-dropdown="${index}"]`,
    ) as HTMLElement | null;
    if (!button || !dropdown) return;

    const rect = button.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 120);
    dropdown.style.position = 'fixed';
    dropdown.style.top = `${rect.bottom + 4}px`;
    dropdown.style.left = `${rect.right - minWidth}px`;
    dropdown.style.right = 'auto';
    dropdown.style.minWidth = `${minWidth}px`;
    dropdown.style.width = `${minWidth}px`;
    dropdown.style.zIndex = '10000';
  }

  isTableActionOpen(index: number): boolean {
    return this.openTableActionRow() === index;
  }

  closeTableActionDropdown(): void {
    if (this.openTableActionRow() !== null) {
      this.openTableActionRow.set(null);
      this.cdr.markForCheck();
    }
  }

  editDescriptorFromTable(index: number): void {
    this.closeTableActionDropdown();
    this.openDescriptorEditor();
    this.selectedEditorRowIndex.set(index);
    this.cdr.markForCheck();
  }

  deleteDescriptorFromTable(index: number): void {
    this.closeTableActionDropdown();
    this.deleteDescriptorRow(index);
  }

  openDescriptorEditor(): void {
    this.editorRows = deepCopyRows(this.descriptorRows);
    refreshAttributeNameDuplicateFlags(this.editorRows);
    this.closeEditorDropdown();
    this.selectedEditorRowIndex.set(this.editorRows.length > 0 ? 0 : null);
    this.isDescriptorEditorOpen = true;
    this.lockBodyScroll();
    this.cdr.detectChanges();
    // Escape provider/main-content stacking (sidebar z-index wins over content).
    queueMicrotask(() => this.mountDescriptorEditorOverlay());
  }

  /** Move overlay to body so it covers the app sidebar when zoomed/narrow. */
  private mountDescriptorEditorOverlay(): void {
    if (!this.isDescriptorEditorOpen) return;
    const overlay = this.elementRef.nativeElement.querySelector(
      '.ngsild-modal-overlay',
    ) as HTMLElement | null;
    if (!overlay || overlay.parentNode === document.body) return;
    document.body.appendChild(overlay);
  }

  closeDescriptorEditor(): void {
    this.isDescriptorEditorOpen = false;
    this.closeEditorDropdown();
    this.editorRows = [];
    this.selectedEditorRowIndex.set(null);
    this.unlockBodyScroll();
    this.cdr.markForCheck();
  }

  private lockBodyScroll(): void {
    document.body.classList.add(MetadataInfoComponent.BODY_SCROLL_LOCK_CLASS);
  }

  private unlockBodyScroll(): void {
    document.body.classList.remove(MetadataInfoComponent.BODY_SCROLL_LOCK_CLASS);
  }

  saveDescriptorEditor(): void {
    if (!this.isDescriptorEditorSaveEnabled()) {
      return;
    }

    this.closeEditorDropdown();

    const saved = saveDataDescriptorDraft(this.editorRows);
    if (!saved) {
      this.expandRowsWithValidationErrors();
      this.editorRows = deepCopyRows(this.editorRows);
      this.cdr.markForCheck();
      return;
    }

    this.descriptorRows = deepCopyRows(
      this.editorRows.filter((row) => !!row.attributeName?.trim()),
    );
    this.isDescriptorEditorOpen = false;
    this.closeEditorDropdown();
    this.editorRows = [];
    this.selectedEditorRowIndex.set(null);
    this.unlockBodyScroll();
    this.emitState();
    this.cdr.markForCheck();
  }

  private expandRowsWithValidationErrors(): void {
    const firstInvalid = this.editorRows.findIndex((row) =>
      this.rowHasValidationErrors(row),
    );
    if (firstInvalid >= 0) {
      this.selectedEditorRowIndex.set(firstInvalid);
    } else if (this.editorRows.length > 0) {
      this.selectedEditorRowIndex.set(0);
    } else {
      this.selectedEditorRowIndex.set(null);
    }
  }

  private rowHasValidationErrors(row: DataDescriptorFormRow): boolean {
    const v = row.validations;
    return (
      v.isAttributeNameValid ||
      v.isAttributeNameDuplicate ||
      v.isParameterTypeValid ||
      v.isDataSchemaTypeValid ||
      v.isDataSchemaNumberTypeValid ||
      v.isDataSchemaNumberUnitCodeTypeValid ||
      v.isDataSchemaNumberUnitTextValid ||
      v.isDataSchemaOthersDescriptionValid
    );
  }

  isDescriptorEditorSaveEnabled(): boolean {
    return canSaveDataDescriptors(this.editorRows);
  }

  isAddEditorRowEnabled(): boolean {
    // All existing rows must be complete before a new one can be added
    return this.editorRows.every((row, i) =>
      isDataDescriptorRowComplete(row, this.editorRows, i),
    );
  }

  addEditorRow(): void {
    if (!this.isAddEditorRowEnabled()) {
      return;
    }

    const newIndex = this.editorRows.length;
    this.editorRows = addDataDescriptorRow(this.editorRows);
    this.closeEditorDropdown();
    this.selectedEditorRowIndex.set(newIndex);
    this.cdr.markForCheck();
  }

  removeEditorRow(index: number): void {
    this.editorRows = removeDataDescriptorRow(this.editorRows, index);
    this.closeEditorDropdown();
    const selected = this.selectedEditorRowIndex();
    if (this.editorRows.length === 0) {
      this.selectedEditorRowIndex.set(null);
    } else if (selected === null) {
      this.selectedEditorRowIndex.set(0);
    } else if (selected === index) {
      this.selectedEditorRowIndex.set(Math.min(index, this.editorRows.length - 1));
    } else if (selected > index) {
      this.selectedEditorRowIndex.set(selected - 1);
    }
    refreshAttributeNameDuplicateFlags(this.editorRows);
    this.cdr.markForCheck();
  }

  selectEditorRow(index: number): void {
    if (this.selectedEditorRowIndex() !== index) {
      this.closeEditorDropdown();
    }
    this.selectedEditorRowIndex.set(index);
    this.cdr.markForCheck();
  }

  isEditorRowSelected(index: number): boolean {
    return this.selectedEditorRowIndex() === index;
  }

  onEditorDetailScroll(): void {
    if (this.openEditorDropdown()) {
      this.closeEditorDropdown();
      this.cdr.markForCheck();
    }
  }

  getAccordionTitle(row: DataDescriptorFormRow, index: number): string {
    const name = row.attributeName?.trim();
    return name || `Attribute name ${index + 1}`;
  }

  getAccordionSubtitle(row: DataDescriptorFormRow): string {
    const param = this.getParameterTypeLabel(row);
    const schema = this.getSchemaTypeLabel(row);
    if (param && schema) return `${param} · ${schema}`;
    if (param) return param;
    if (schema) return schema;
    return 'Not configured';
  }

  // ── Editor dropdown helpers ──────────────────────────────────────────────

  editorDropdownKey(rowIndex: number, field: string): string {
    return `${rowIndex}:${field}`;
  }

  isEditorDropdownOpen(rowIndex: number, field: string): boolean {
    return this.openEditorDropdown() === this.editorDropdownKey(rowIndex, field);
  }

  toggleEditorDropdown(event: MouseEvent, rowIndex: number, field: string): void {
    event.stopPropagation();
    const key = this.editorDropdownKey(rowIndex, field);
    const wasOpen = this.openEditorDropdown() === key;
    if (wasOpen) {
      this.closeEditorDropdown();
      this.cdr.markForCheck();
      return;
    }

    this.teardownEditorDropdownPortal();
    this.openEditorDropdown.set(key);
    this.cdr.detectChanges();

    queueMicrotask(() =>
      this.mountEditorDropdownPanel(
        event.currentTarget as HTMLElement,
        rowIndex,
        field,
      ),
    );
  }

  private mountEditorDropdownPanel(
    trigger: HTMLElement,
    rowIndex: number,
    field: string,
  ): void {
    const panel = this.elementRef.nativeElement.querySelector(
      `[data-ngsild-editor-dropdown="${rowIndex}:${field}"]`,
    ) as HTMLElement | null;
    if (!panel) return;

    this.teardownEditorDropdownPortal();
    this.editorDropdownPortal = panel;
    panel.classList.add('ngsild-editor-dropdown-panel--fixed');
    document.body.appendChild(panel);
    this.positionEditorDropdownPanel(trigger, panel);
  }

  private positionEditorDropdownPanel(
    trigger: HTMLElement,
    panel: HTMLElement,
  ): void {
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const panelMaxHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 12;
    const spaceAbove = rect.top - gap - 12;
    const openUpward = spaceBelow < 120 && spaceAbove > spaceBelow;

    panel.style.position = 'fixed';
    panel.style.left = `${rect.left}px`;
    panel.style.width = `${rect.width}px`;
    panel.style.minWidth = `${rect.width}px`;
    panel.style.right = 'auto';
    panel.style.zIndex = '100020';
    panel.style.overflowY = 'auto';
    panel.style.display = 'block';
    panel.style.maxHeight = `${Math.min(
      panelMaxHeight,
      Math.max(140, openUpward ? spaceAbove : spaceBelow),
    )}px`;

    if (openUpward) {
      panel.style.top = 'auto';
      panel.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      panel.style.top = `${rect.bottom + gap}px`;
      panel.style.bottom = 'auto';
    }
  }

  private teardownEditorDropdownPortal(): void {
    const panel = this.editorDropdownPortal;
    if (!panel) return;

    panel.classList.remove('ngsild-editor-dropdown-panel--fixed');
    panel.removeAttribute('style');
    if (panel.parentNode === document.body) {
      panel.remove();
    }
    this.editorDropdownPortal = null;
  }

  private closeEditorDropdown(): void {
    if (this.editorDropdownPortal) {
      this.editorDropdownPortal.classList.remove('ngsild-editor-dropdown-panel--fixed');
      this.editorDropdownPortal.removeAttribute('style');
    }
    this.editorDropdownPortal = null;
    this.openEditorDropdown.set(null);
  }

  selectEditorParameterType(rowIndex: number, item: DropdownItem): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    row.parameterType = selectDropdownValue(item.item_id);
    row.validations.isParameterTypeValid = false;
    this.closeEditorDropdown();
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  selectEditorSchemaType(rowIndex: number, item: DropdownItem): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    row.dataSchemaType = selectDropdownValue(item.item_id);
    row.attributeObject = {};
    row.validations.isDataSchemaTypeValid = false;
    row.validations.isDataSchemaNumberTypeValid = false;
    row.validations.isDataSchemaNumberUnitCodeTypeValid = false;
    row.validations.isDataSchemaNumberUnitTextValid = false;
    row.validations.isDataSchemaOthersDescriptionValid = false;
    this.closeEditorDropdown();
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  getParameterTypeLabel(row: DataDescriptorFormRow): string {
    if (!Array.isArray(row.parameterType)) return '';
    return row.parameterType[0]?.item_text ?? '';
  }

  getSchemaTypeLabel(row: DataDescriptorFormRow): string {
    if (!Array.isArray(row.dataSchemaType)) return '';
    return row.dataSchemaType[0]?.item_text ?? '';
  }

  isParameterTypeSelected(row: DataDescriptorFormRow, itemId: string): boolean {
    if (!Array.isArray(row.parameterType)) return false;
    return row.parameterType[0]?.item_id === itemId;
  }

  isSchemaTypeSelected(row: DataDescriptorFormRow, itemId: string): boolean {
    if (!Array.isArray(row.dataSchemaType)) return false;
    return row.dataSchemaType[0]?.item_id === itemId;
  }

  isNumberSchemaRow(row: DataDescriptorFormRow): boolean {
    return isNumberSchema(row);
  }

  hasSchemaSelected(row: DataDescriptorFormRow): boolean {
    return !!getSchemaTypeId(row);
  }

  onAttributeNameChange(rowIndex: number, value: string): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    row.attributeName = value;
    row.validations.isAttributeNameValid = false;
    refreshAttributeNameDuplicateFlags(this.editorRows);
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  onDescriptionChange(rowIndex: number, value: string): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    if (isNumberSchema(row)) {
      row.attributeObject.description = value;
      row.validations.isDataSchemaNumberTypeValid = false;
    } else {
      row.attributeObject.othersDescription = value;
      row.validations.isDataSchemaOthersDescriptionValid = false;
    }
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  onUnitCodeChange(rowIndex: number, value: string): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    row.attributeObject.unitCode = value;
    row.validations.isDataSchemaNumberUnitCodeTypeValid = false;
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  onUnitTextChange(rowIndex: number, value: string): void {
    const row = this.editorRows[rowIndex];
    if (!row) return;
    row.attributeObject.unitText = value;
    row.validations.isDataSchemaNumberUnitTextValid = false;
    this.editorRows = [...this.editorRows];
    this.cdr.markForCheck();
  }

  getDescriptionValue(row: DataDescriptorFormRow): string {
    if (isNumberSchema(row)) return row.attributeObject.description ?? '';
    return row.attributeObject.othersDescription ?? '';
  }

  /** Returns the saved rows for display in the parent form and for submission. */
  getDescriptorsData(): DataDescriptorFormRow[] {
    return this.descriptorRows;
  }

  /** Validate all saved rows (called from parent before submit). */
  validateDescriptors(): boolean {
    return validateDataDescriptors(this.descriptorRows);
  }

  /** Sample data for create-item — null when the user skipped optional sample data. */
  getSampleData(): unknown | null {
    return this.dataSample;
  }

  validateAdditionalInfo(): boolean {
    return this.additionalInfoRef()?.validate() ?? true;
  }

  getAdditionalInfoItems(): AdditionalInfoFeedbackItem[] {
    return this.additionalInfoRef()?.getFeedbackItems() ?? [];
  }

  applyAdditionalInfo(state: TemplateAdditionalInfoState): void {
    this.additionalInfoRef()?.applyTemplate(state);
  }

  get hasSampleData(): boolean {
    return this.dataSample != null;
  }

  get formattedSampleDataPreview(): string {
    return formatSampleDataJson(this.dataSample);
  }

  // ── Sample Data editor (JSON code editor) ────────────────────────────────

  openSamplePreview(): void {
    if (!this.hasSampleData) {
      return;
    }
    if (this.isSampleEditorOpen) {
      this.closeSampleEditor();
    }
    this.isSamplePreviewOpen = true;
    this.lockBodyScroll();
    this.cdr.detectChanges();
    queueMicrotask(() => this.mountSamplePreviewOverlay());
  }

  private mountSamplePreviewOverlay(): void {
    if (!this.isSamplePreviewOpen) return;
    const overlay = this.elementRef.nativeElement.querySelector(
      '[data-ngsild-sample-preview-overlay]',
    ) as HTMLElement | null;
    if (!overlay || overlay.parentNode === document.body) return;
    document.body.appendChild(overlay);
  }

  closeSamplePreview(): void {
    this.isSamplePreviewOpen = false;
    this.unlockBodyScroll();
    this.cdr.markForCheck();
  }

  editSampleFromPreview(): void {
    this.isSamplePreviewOpen = false;
    this.openSampleEditor();
  }

  openSampleEditor(): void {
    if (this.isSamplePreviewOpen) {
      this.isSamplePreviewOpen = false;
    }
    this.sampleEditorError = null;
    this.sampleUploadError = null;
    this.sampleEditorText = this.dataSample != null
      ? formatSampleDataJson(this.dataSample)
      : '{\n  \n}';
    this.isSampleEditorOpen = true;
    this.lockBodyScroll();
    this.cdr.detectChanges();
    queueMicrotask(() => this.mountSampleEditorOverlay());
  }

  private mountSampleEditorOverlay(): void {
    if (!this.isSampleEditorOpen) return;
    const overlay = this.elementRef.nativeElement.querySelector(
      '[data-ngsild-sample-overlay]',
    ) as HTMLElement | null;
    if (!overlay || overlay.parentNode === document.body) return;
    document.body.appendChild(overlay);
  }

  closeSampleEditor(): void {
    this.isSampleEditorOpen = false;
    this.sampleEditorText = '';
    this.sampleEditorError = null;
    this.unlockBodyScroll();
    this.cdr.markForCheck();
  }

  onSampleEditorTextChange(value: string): void {
    this.sampleEditorText = value;
    this.sampleEditorError = null;
    this.cdr.markForCheck();
  }

  saveSampleEditor(): void {
    const text = this.sampleEditorText.trim();
    if (!text) {
      this.sampleEditorError = 'JSON is required';
      this.cdr.markForCheck();
      return;
    }
    if (utf8ByteLength(text) > this.sampleJsonMaxBytes) {
      this.sampleEditorError = 'JSON size must be 2 MB or less';
      this.cdr.markForCheck();
      return;
    }
    try {
      this.dataSample = parseSampleDataJsonFile(text);
      this.sampleUploadedFileName = null;
      this.sampleUploadError = null;
      this.sampleEditorError = null;
      this.isSampleEditorOpen = false;
      this.sampleEditorText = '';
      this.unlockBodyScroll();
      this.emitState();
    } catch {
      this.sampleEditorError = 'Invalid JSON. Must be a single object.';
    }
    this.cdr.markForCheck();
  }

  clearSampleData(): void {
    this.closeSamplePreview();
    this.dataSample = null;
    this.sampleUploadedFileName = null;
    this.sampleUploadError = null;
    this.sampleEditorError = null;
    this.emitState();
    this.cdr.markForCheck();
  }

  onSampleJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    this.sampleUploadError = null;

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.json') && file.type !== 'application/json') {
      this.sampleUploadError = 'Please upload a .json file';
      this.cdr.markForCheck();
      return;
    }

    if (file.size > this.sampleJsonMaxBytes) {
      this.sampleUploadError = 'File size must be 2 MB or less';
      this.cdr.markForCheck();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const parsed = parseSampleDataJsonFile(text);
        this.dataSample = parsed;
        this.sampleUploadedFileName = file.name;
        this.sampleUploadError = null;
        this.emitState();
      } catch {
        this.sampleUploadError = 'Invalid JSON file. Must be a single object.';
      }
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.sampleUploadError = 'Failed to read the file';
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
  }

  onAddSampleDataClick(): void {
    if (this.hasSampleData) {
      this.sampleUploadError =
        'Delete the current JSON to upload or add new JSON';
      this.cdr.markForCheck();
      return;
    }
    this.openSampleEditor();
  }

  triggerSampleJsonUpload(fileInput: HTMLInputElement): void {
    if (this.hasSampleData) {
      this.sampleUploadError =
        'Delete the current JSON to upload or add new JSON';
      this.cdr.markForCheck();
      return;
    }
    this.sampleUploadError = null;
    fileInput.click();
  }

  // ── Shared helpers ───────────────────────────────────────────────────────

  /** Prefill from JSON upload / preview step. */
  async applyValue(value: NgsildMetadataInfo): Promise<void> {
    if (Object.keys(this.fileFormatMap).length === 0) {
      await this.loadFileFormatMap();
    }
    this.form.patchValue({
      tags: value.tags ?? [],
      yearRange: value.yearRange,
      geoCoverage: value.geoCoverage ?? '',
      uploadFrequency: value.uploadFrequency ?? '',
      license: value.license ?? '',
      otherLicense: value.otherLicense ?? '',
      datasetType: this.isOgcFeature() ? OGC_FEATURE_DATASET_TYPE : (value.datasetType ?? ''),
    });
    if (this.isOgcFeature()) {
      this.applyOgcFeatureDefaults();
    } else {
      const datasetType = value.datasetType ?? '';
      if (datasetType) {
        this.fileFormatOptions = this.fileFormatMap[datasetType] ?? [];
        this.form.controls.fileFormat.enable();
        const resolved = matchFileFormatToOptionValue(
          value.fileFormat ?? '',
          this.fileFormatOptions.map((o) => o.value),
        );
        this.form.controls.fileFormat.setValue(resolved);
      } else {
        this.fileFormatOptions = [];
        this.form.controls.fileFormat.disable();
        this.form.controls.fileFormat.setValue('');
      }
    }
    const license = value.license ?? '';
    const otherLicenseControl = this.form.get('otherLicense');
    if (license === 'Other') {
      otherLicenseControl?.setValidators([Validators.required]);
    } else {
      otherLicenseControl?.clearValidators();
    }
    otherLicenseControl?.updateValueAndValidity({ emitEvent: false });

    if (Array.isArray(value.dataDescriptors) && value.dataDescriptors.length > 0) {
      this.descriptorRows = deepCopyRows(value.dataDescriptors);
    }

    if (value.dataSample != null) {
      this.dataSample = value.dataSample;
      this.sampleUploadedFileName = null;
    }

    this.form.markAsPristine();
    this.emitState();
    this.cdr.markForCheck();
  }

  /** Reset to empty defaults after successful submit. */
  resetForm(): void {
    this.tagInput = '';
    this.openDropdown.set(null);
    this.closeEditorDropdown();
    this.teardownEditorDropdownPortal();
    this.fileFormatOptions = [];
    clearDataDescriptorDraft();
    this.descriptorRows = [createEmptyDataDescriptorRow()];
    this.editorRows = [];
    this.selectedEditorRowIndex.set(null);
    this.isDescriptorEditorOpen = false;
    this.dataSample = null;
    this.sampleUploadedFileName = null;
    this.sampleUploadError = null;
    this.sampleEditorError = null;
    this.sampleEditorText = '';
    this.isSampleEditorOpen = false;
    this.isSamplePreviewOpen = false;
    this.additionalInfoRef()?.resetForm();
    this.unlockBodyScroll();
    this.form.reset({
      tags: [],
      yearRange: null,
      geoCoverage: '',
      uploadFrequency: '',
      license: '',
      otherLicense: '',
      datasetType: '',
      fileFormat: '',
    });
    this.form.controls.fileFormat.disable();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    if (this.isOgcFeature()) {
      this.applyOgcFeatureDefaults();
    }
    this.emitState();
    this.cdr.markForCheck();
  }

  /**
   * Sets dataset type to "semi Structured" and file format to "gpkg" and makes
   * those two fields the only selectable options. Called whenever isOgcFeature is true.
   */
  private applyOgcFeatureDefaults(): void {
    this.fileFormatOptions = [{ value: OGC_FEATURE_FILE_FORMAT, label: OGC_FEATURE_FILE_FORMAT }];
    this.form.controls.fileFormat.enable();
    this.form.patchValue({
      datasetType: OGC_FEATURE_DATASET_TYPE,
      fileFormat: OGC_FEATURE_FILE_FORMAT,
    });
    this.cdr.markForCheck();
  }

  private async loadFileFormatMap(): Promise<void> {
    const paths: Record<string, string> = {
      [DATASET_TYPE_STRUCTURED]: '/documents/dataset-structured-fileformat.json',
      [DATASET_TYPE_SEMI]: '/documents/dataset-semi-structured-fileformat..json',
      [DATASET_TYPE_UNSTRUCTURED]: '/documents/dataset-unstructured-fileformat..json',
    };

    const entries = await Promise.all(
      Object.entries(paths).map(async ([type, path]) => {
        try {
          const labels = await firstValueFrom(this.http.get<string[]>(path));
          const options = (Array.isArray(labels) ? labels : []).map((label) => ({
            value: label,
            label,
          }));
          return [type, options] as const;
        } catch {
          return [type, []] as const;
        }
      }),
    );

    this.fileFormatMap = Object.fromEntries(entries);

    if (this.isOgcFeature()) {
      this.applyOgcFeatureDefaults();
    } else {
      const datasetType = this.form.controls.datasetType.value;
      if (datasetType) {
        this.fileFormatOptions = this.fileFormatMap[datasetType] ?? [];
        this.form.controls.fileFormat.enable();
      }
    }
    this.cdr.markForCheck();
  }

  private emitState(): void {
    this.validChange.emit(this.form.valid);
    const raw = this.form.getRawValue();
    this.valueChange.emit({
      tags: raw.tags,
      yearRange: raw.yearRange,
      geoCoverage: raw.geoCoverage,
      uploadFrequency: raw.uploadFrequency,
      license: raw.license,
      otherLicense: raw.otherLicense,
      datasetType: raw.datasetType,
      fileFormat: raw.fileFormat,
      dataDescriptors: this.descriptorRows,
      dataSample: this.dataSample,
    });
  }
}
